import React, { useState } from "react";
import DoctorSelect from "./DoctorSelect";
import { usePatientForm } from "../../hooks/usePatientForm";
import colorTheme from "../common/color";
import { Patient } from "./PatientCard";
interface PatientRegistrationFormProps {
  onPatientUpdated?: (patient: Patient) => void;
}
const PatientRegistrationForm: React.FC<PatientRegistrationFormProps> = ({
  onPatientUpdated,
}) => {
  const {
    formData,
    setFormData,
    errors,
    isLoading,
    isSuccess,
    createdPatient,
    doctors,
    handleInputChange,
    handleSubmit,
    clinics,
    user,
    resetForm,
    setIsSuccess,
  } = usePatientForm();

  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>(
    {}
  ); // Add this line
  const [currentSection, setCurrentSection] = useState(1);

  const bloodTypes = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const relationships = [
    "Spouse",
    "Parent",
    "Child",
    "Sibling",
    "Friend",
    "Other",
  ];

  // Field Error Component
  const FieldError: React.FC<{ error: string }> = ({ error }) => (
    <div
      style={{
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: "8px",
        padding: "8px 12px",
        marginBottom: "8px",
        color: "#dc2626",
        fontSize: "14px",
        display: "flex",
        alignItems: "center",
        animation: "slideDown 0.3s ease-out",
      }}
    >
      <span style={{ marginRight: "6px" }}>⚠️</span>
      {error}
    </div>
  );

  // Enhanced validation function that returns detailed error info
  const validateCurrentSectionWithDetails = (): {
    isValid: boolean;
    errors: Record<string, string>;
  } => {
    let sectionErrors: Record<string, string> = {};

    switch (currentSection) {
      case 1:
        if (!formData.firstName.trim()) {
          sectionErrors.firstName = "First name is required";
        }
        if (!formData.lastName.trim()) {
          sectionErrors.lastName = "Last name is required";
        }
        if (!formData.birthDate) {
          sectionErrors.birthDate = "Birth date is required";
        } else {
          const birthDate = new Date(formData.birthDate);
          const today = new Date();
          if (birthDate > today) {
            sectionErrors.birthDate = "Birth date cannot be in the future";
          }
        }
        break;

      case 2:
        if (!formData.phone.trim()) {
          sectionErrors.phone = "Phone number is required";
        } else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
          sectionErrors.phone = "Please enter a valid phone number";
        }

        if (!formData.address.trim()) {
          sectionErrors.address = "Address is required";
        }

        // REMOVED: Emergency contact required validations

        // Optional validations - only if fields are filled
        if (
          formData.emergencyContactPhone &&
          !/^\+?[\d\s\-\(\)]+$/.test(formData.emergencyContactPhone)
        ) {
          sectionErrors.emergencyContactPhone =
            "Please enter a valid emergency contact phone";
        }

        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
          sectionErrors.email = "Please enter a valid email address";
        }
        break;

      case 3:
        break;

      case 4:
        break;
    }

    return {
      isValid: Object.keys(sectionErrors).length === 0,
      errors: sectionErrors,
    };
  };

  const nextSection = () => {
    setSectionErrors({});
    let newErrors: Record<string, string> = {};

    if (currentSection === 1) {
      if (!formData.firstName || !formData.firstName.trim()) {
        newErrors.firstName = "First name is required";
      }
      if (!formData.lastName || !formData.lastName.trim()) {
        newErrors.lastName = "Last name is required";
      }
      if (!formData.birthDate) {
        newErrors.birthDate = "Birth date is required";
      } else {
        const birthDate = new Date(formData.birthDate);
        const today = new Date();
        if (birthDate > today) {
          newErrors.birthDate = "Birth date cannot be in the future";
        }
      }
    }

    if (currentSection === 2) {
      if (!formData.phone || !formData.phone.trim()) {
        newErrors.phone = "Phone number is required";
      } else if (!/^\+?[\d\s\-\(\)]+$/.test(formData.phone)) {
        newErrors.phone = "Please enter a valid phone number";
      }

      if (!formData.address || !formData.address.trim()) {
        newErrors.address = "Address is required";
      }

      // REMOVED: Emergency contact validations (now optional)

      if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Please enter a valid email address";
      }

      // Optional validation: if emergency phone is provided, validate format
      if (
        formData.emergencyContactPhone &&
        !/^\+?[\d\s\-\(\)]+$/.test(formData.emergencyContactPhone)
      ) {
        newErrors.emergencyContactPhone =
          "Please enter a valid emergency contact phone";
      }
    }

    if (Object.keys(newErrors).length === 0) {
      if (currentSection < 4) {
        setCurrentSection(currentSection + 1);
        setTimeout(() => {
          const formCard = document.getElementById("form-card");
          if (formCard) {
            formCard.scrollIntoView({
              behavior: "smooth",
              block: "start",
            });
          }
        }, 100);
      }
    } else {
      console.log("Setting errors:", newErrors);
      setSectionErrors(newErrors);
      setTimeout(() => {
        const firstErrorField = Object.keys(newErrors)[0];
        const firstErrorElement = document.getElementById(firstErrorField);
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          setTimeout(() => {
            firstErrorElement.focus();
          }, 300);
        }
      }, 100);
      setTimeout(() => {
        setSectionErrors({});
      }, 5000);
    }
  };

  const prevSection = () => {
    if (currentSection > 1) setCurrentSection(currentSection - 1);
  };

  // Function to handle "Register Another Patient" - resets form and goes to section 1
  const handleRegisterAnother = () => {
    setIsSuccess(false);
    setCurrentSection(1); // Reset to first section
    resetForm(); // Reset form data
  };
  const handleViewPatient = () => {
    if (!createdPatient) {
      console.error("No created patient data available");
      return;
    }

    // Convert LaravelPatient to Patient format for the modal
    const patientForModal = {
      id: createdPatient.id,
      first_name: createdPatient.first_name,
      last_name: createdPatient.last_name,
      full_name:
        createdPatient.full_name ||
        `${createdPatient.first_name} ${createdPatient.last_name}`,
      father_name: createdPatient.father_name,
      mother_name: createdPatient.mother_name,
      birth_date: createdPatient.birth_date,
      age: createdPatient.age,
      gender: createdPatient.gender,
      blood_type: createdPatient.blood_type,
      phone: createdPatient.phone,
      email: createdPatient.email,
      address: createdPatient.address,
      emergency_contact: createdPatient.emergency_contact,
      medical_notes: createdPatient.medical_notes,
      allergies: createdPatient.allergies,
      chronic_conditions: createdPatient.chronic_conditions,
      clinic_id: createdPatient.clinic_id,
      clinic: createdPatient.clinic,
      primary_doctor_id: createdPatient.primary_doctor_id,
      primary_doctor: createdPatient.primary_doctor,
      insurance_provider: createdPatient.insurance_provider,
      insurance_number: createdPatient.insurance_number,
      is_active: createdPatient.is_active,
      created_at: createdPatient.created_at,
      updated_at: createdPatient.updated_at,
      is_starred: createdPatient.is_starred,
      star_reason: createdPatient.star_reason,
      starred_by: createdPatient.starred_by,
      starred_at: createdPatient.starred_at,
    };

    // ✅ Call the callback to open the modal in parent component
    if (onPatientUpdated) {
      onPatientUpdated(patientForModal);
    }

    // ✅ DO NOT reset form here - keep success screen visible
    // User can click "Register Another" if they want to reset
    // The modal will open on top of the success screen
  };
  // Function to handle "Book Appointment" - navigate to appointments page
  const handleBookAppointment = () => {
    if (!createdPatient) {
      console.error("No created patient data available");
      return;
    }

    // Navigate to appointments page with patient pre-filled
    const currentUrl = new URL(window.location.href);
    currentUrl.pathname = "/appointments";
    currentUrl.searchParams.set("bookForPatient", createdPatient.id.toString());
    if (formData.primaryDoctorId) {
      currentUrl.searchParams.set(
        "doctorId",
        formData.primaryDoctorId.toString()
      );
    }
    // Navigate to appointments page
    window.location.href = currentUrl.toString();
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: "100vh",
    background: colorTheme.bgGradient,
    padding: "32px 16px",
    fontFamily: '"Poppins", "Raleway", "Segoe UI", Roboto, Arial, sans-serif',
  };

  const mainCardStyle: React.CSSProperties = {
    maxWidth: "1000px",
    margin: "0 auto",
    background: colorTheme.cardBg,
    borderRadius: "16px",
    boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
    overflow: "hidden",
  };

  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    marginBottom: "48px",
  };

  const titleStyle: React.CSSProperties = {
    fontSize: "42px",
    fontWeight: "bold",
    background: colorTheme.bgGradient,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
    marginBottom: "8px",
  };

  const subtitleStyle: React.CSSProperties = {
    color: colorTheme.gray500,
    fontSize: "16px",
  };

  const formContentStyle: React.CSSProperties = {
    padding: "56px",
  };

  const sectionHeaderStyle: React.CSSProperties = {
    borderLeft: `4px solid ${colorTheme.primary}`,
    paddingLeft: "16px",
    marginBottom: "40px",
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: "24px",
    fontWeight: "600",
    color: colorTheme.gray900,
    marginBottom: "4px",
  };

  const sectionDescStyle: React.CSSProperties = {
    color: colorTheme.gray500,
    fontSize: "14px",
  };

  const twoColGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: "60px",
    marginBottom: "32px",
  };

  const threeColGridStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "60px",
    marginBottom: "32px",
  };

  const inputGroupStyle: React.CSSProperties = {
    marginBottom: "28px",
  };

  const labelStyle: React.CSSProperties = {
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    color: colorTheme.gray600,
    marginBottom: "10px",
  };

  const getInputStyle = (hasError: boolean): React.CSSProperties => ({
    width: "100%",
    padding: "14px 18px",
    border: hasError
      ? `2px solid ${colorTheme.error}`
      : `2px solid ${colorTheme.gray200}`,
    borderRadius: "12px",
    fontSize: "16px",
    background: hasError ? "#fef2f2" : colorTheme.cardBg,
    transition: "all 0.2s ease",
    outline: "none",
  });

  const errorStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    color: colorTheme.error,
    fontSize: "14px",
    marginTop: "6px",
  };

  const emergencyBoxStyle: React.CSSProperties = {
    background: colorTheme.emergencyBg,
    padding: "32px",
    borderRadius: "12px",
    marginTop: "32px",
  };

  const emergencyTitleStyle: React.CSSProperties = {
    fontSize: "18px",
    fontWeight: "600",
    color: colorTheme.gray900,
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
  };

  const navigationStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: "40px",
    borderTop: `1px solid ${colorTheme.gray200}`,
    marginTop: "40px",
    gap: "16px", // Add space between buttons
    flexWrap: "wrap", // Allow wrapping on very small screens
  };

  const getPrevButtonStyle = (disabled: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    padding: "12px 24px",
    background: disabled ? colorTheme.gray100 : colorTheme.gray500,
    color: disabled ? colorTheme.gray300 : "white",
    border: "none",
    borderRadius: "12px",
    fontSize: "16px",
    fontWeight: "500",
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease",
  });

  const getNextButtonStyle = (isSubmit: boolean): React.CSSProperties => {
    return {
      display: "flex",
      alignItems: "center",
      padding: "12px 32px",
      background: isSubmit ? colorTheme.successGradient : colorTheme.bgGradient,
      color: "white",
      border: "none",
      borderRadius: "12px",
      fontSize: "16px",
      fontWeight: "600",
      cursor: isLoading ? "not-allowed" : "pointer",
      transition: "all 0.2s ease",
      transform: "scale(1)",
      boxShadow: `0 4px 15px rgba(102, 126, 234, 0.4)`,
      opacity: isLoading ? 0.6 : 1,
    };
  };

  // Validation function for each section
  const validateCurrentSection = (): boolean => {
    const result = validateCurrentSectionWithDetails();
    setSectionErrors(result.errors);
    return result.isValid;
  };

  // Silent validation check for button styling (doesn't set errors)
  const validateCurrentSectionQuiet = (): boolean => {
    switch (currentSection) {
      case 1:
        return !!(
          formData.firstName.trim() &&
          formData.lastName.trim() &&
          formData.birthDate &&
          new Date(formData.birthDate) <= new Date()
        );

      case 2:
        const emailValid =
          !formData.email || /\S+@\S+\.\S+/.test(formData.email);
        const phoneValid =
          !formData.phone || /^\+?[\d\s\-\(\)]+$/.test(formData.phone);
        const emergencyPhoneValid =
          !formData.emergencyContactPhone ||
          /^\+?[\d\s\-\(\)]+$/.test(formData.emergencyContactPhone);

        return !!(
          (
            formData.phone.trim() &&
            formData.address.trim() &&
            emailValid &&
            phoneValid &&
            emergencyPhoneValid
          )
          // REMOVED: Emergency contact field requirements
        );

      case 3:
        return true;

      case 4:
        return true;

      default:
        return true;
    }
  };

  // Success screen
  if (isSuccess) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: colorTheme.bgGradient,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "16px",
        }}
      >
        <div
          style={{
            maxWidth: "500px",
            background: colorTheme.cardBg,
            borderRadius: "16px",
            boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
            padding: "48px 32px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              width: "64px",
              height: "64px",
              background: colorTheme.successGradient,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 24px auto",
              fontSize: "28px",
              color: "white",
            }}
          >
            ✓
          </div>
          <h3
            style={{
              fontSize: "28px",
              fontWeight: "bold",
              color: colorTheme.gray900,
              marginBottom: "12px",
            }}
          >
            Success!
          </h3>
          <p
            style={{
              color: colorTheme.gray500,
              marginBottom: "24px",
              fontSize: "16px",
            }}
          >
            Patient has been registered successfully and added to the system.
          </p>

          {/* Patient Information Summary */}
          {createdPatient && (
            <div
              style={{
                background: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "12px",
                padding: "20px",
                marginBottom: "24px",
                textAlign: "left",
              }}
            >
              <h4
                style={{
                  fontSize: "16px",
                  fontWeight: "600",
                  color: "#166534",
                  marginBottom: "12px",
                  textAlign: "center",
                }}
              >
                📋 Patient Details
              </h4>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "12px",
                  fontSize: "14px",
                }}
              >
                <div>
                  <strong>Name:</strong>{" "}
                  {createdPatient.full_name ||
                    `${createdPatient.first_name} ${createdPatient.last_name}`}
                </div>
                <div>
                  <strong>Phone:</strong> {createdPatient.phone}
                </div>
                {createdPatient.email && (
                  <div style={{ gridColumn: "span 2" }}>
                    <strong>Email:</strong> {createdPatient.email}
                  </div>
                )}
                <div
                  style={{
                    gridColumn: "span 2",
                    marginTop: "8px",
                    fontSize: "12px",
                    color: "#059669",
                  }}
                >
                  {/* <strong>Patient ID:</strong> #{createdPatient.id} */}
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              marginBottom: "24px",
            }}
          >
            {/* Primary Action - View Patient Details */}
            {createdPatient && (
              <button
                onClick={handleViewPatient}
                style={{
                  width: "100%",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  color: "white",
                  border: "none",
                  padding: "14px 24px",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 25px rgba(102, 126, 234, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                👁️ View Patient Details
              </button>
            )}

            {/* Secondary Action - Book Appointment */}
            {createdPatient && (
              <button
                onClick={handleBookAppointment}
                style={{
                  width: "100%",
                  background: colorTheme.successGradient,
                  color: "white",
                  border: "none",
                  padding: "14px 24px",
                  borderRadius: "12px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 25px rgba(16, 185, 129, 0.4)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                📅 Book Appointment
              </button>
            )}

            {/* Tertiary Action - Register Another */}
            <button
              onClick={handleRegisterAnother}
              style={{
                width: "100%",
                background: "#e5e7eb",
                color: "#374151",
                border: "none",
                padding: "14px 24px",
                borderRadius: "12px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#d1d5db";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#e5e7eb";
              }}
            >
              ➕ Register Another Patient
            </button>
          </div>

          {/* Quick Tip */}
          <div
            style={{
              padding: "12px",
              background: "#f0fdf4",
              border: "1px solid #bbf7d0",
              borderRadius: "8px",
              fontSize: "12px",
              color: "#166534",
            }}
          >
            💡 <strong>Quick Tip:</strong> Clicking "Book Appointment Now" will
            take you to the appointments page with this patient's information
            ready to schedule.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>Patient Registration</h1>
          <p style={subtitleStyle}>
            Complete all sections to register a new patient
          </p>
        </div>

        {/* Form Card */}
        <div style={mainCardStyle} id="form-card">
          <div style={formContentStyle} className="form-content">
            {/* Section 1: Personal Information */}
            {currentSection === 1 && (
              <div>
                <div style={sectionHeaderStyle}>
                  <h3 style={sectionTitleStyle}>Personal Information</h3>
                  <p style={sectionDescStyle}>
                    Enter the patient's basic details
                  </p>
                </div>

                <div style={twoColGridStyle} className="two-col-grid">
                  <div style={inputGroupStyle}>
                    <label style={labelStyle} htmlFor="firstName">
                      First Name{" "}
                      <span style={{ color: colorTheme.error }}>*</span>
                    </label>
                    {(errors.firstName || sectionErrors.firstName) && (
                      <FieldError
                        error={errors.firstName || sectionErrors.firstName}
                      />
                    )}
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      style={getInputStyle(
                        !!(errors.firstName || sectionErrors.firstName)
                      )}
                      placeholder="Enter first name"
                    />
                  </div>

                  <div style={inputGroupStyle}>
                    <label style={labelStyle} htmlFor="lastName">
                      Last Name{" "}
                      <span style={{ color: colorTheme.error }}>*</span>
                    </label>
                    {(errors.lastName || sectionErrors.lastName) && (
                      <FieldError
                        error={errors.lastName || sectionErrors.lastName}
                      />
                    )}
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      style={getInputStyle(
                        !!(errors.lastName || sectionErrors.lastName)
                      )}
                      placeholder="Enter last name"
                    />
                  </div>
                </div>

                <div style={twoColGridStyle} className="two-col-grid">
                  <div style={inputGroupStyle}>
                    <label style={labelStyle} htmlFor="fatherName">
                      Father's Name
                    </label>
                    {(errors.fatherName || sectionErrors.fatherName) && (
                      <FieldError
                        error={errors.fatherName || sectionErrors.fatherName}
                      />
                    )}
                    <input
                      type="text"
                      id="fatherName"
                      name="fatherName"
                      value={formData.fatherName}
                      onChange={handleInputChange}
                      style={getInputStyle(
                        !!(errors.fatherName || sectionErrors.fatherName)
                      )}
                      placeholder="Father's full name"
                    />
                  </div>

                  <div style={inputGroupStyle}>
                    <label style={labelStyle} htmlFor="motherName">
                      Mother's Name
                    </label>
                    {(errors.motherName || sectionErrors.motherName) && (
                      <FieldError
                        error={errors.motherName || sectionErrors.motherName}
                      />
                    )}
                    <input
                      type="text"
                      id="motherName"
                      name="motherName"
                      value={formData.motherName}
                      onChange={handleInputChange}
                      style={getInputStyle(
                        !!(errors.motherName || sectionErrors.motherName)
                      )}
                      placeholder="Mother's full name"
                    />
                  </div>
                </div>

                <div style={threeColGridStyle} className="three-col-grid">
                  <div style={inputGroupStyle}>
                    <label style={labelStyle} htmlFor="birthDate">
                      Birth Date{" "}
                      <span style={{ color: colorTheme.error }}>*</span>
                    </label>
                    {(errors.birthDate || sectionErrors.birthDate) && (
                      <FieldError
                        error={errors.birthDate || sectionErrors.birthDate}
                      />
                    )}
                    <input
                      type="date"
                      id="birthDate"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleInputChange}
                      style={getInputStyle(
                        !!(errors.birthDate || sectionErrors.birthDate)
                      )}
                    />
                  </div>

                  <div style={inputGroupStyle}>
                    <label style={labelStyle} htmlFor="gender">
                      Gender <span style={{ color: colorTheme.error }}>*</span>
                    </label>
                    {(errors.gender || sectionErrors.gender) && (
                      <FieldError
                        error={errors.gender || sectionErrors.gender}
                      />
                    )}
                    <select
                      id="gender"
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      style={getInputStyle(
                        !!(errors.gender || sectionErrors.gender)
                      )}
                    >
                      <option value="male">Male</option>
                      <option value="female">Female</option>
                    </select>
                  </div>

                  <div style={inputGroupStyle}>
                    <label style={labelStyle} htmlFor="bloodType">
                      Blood Type
                    </label>
                    <select
                      id="bloodType"
                      name="bloodType"
                      value={formData.bloodType}
                      onChange={handleInputChange}
                      style={getInputStyle(false)}
                    >
                      <option value="">Select blood type</option>
                      {bloodTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Doctor Selection */}
                <div style={inputGroupStyle}>
                  <DoctorSelect
                    value={formData.primaryDoctorId}
                    onChange={(doctorId) =>
                      setFormData((prev) => ({
                        ...prev,
                        primaryDoctorId: doctorId,
                      }))
                    }
                    doctors={doctors}
                    error={errors.primaryDoctorId}
                  />
                </div>
              </div>
            )}

            {/* Section 2: Contact Information */}
            {currentSection === 2 && (
              <div>
                <div style={sectionHeaderStyle}>
                  <h3 style={sectionTitleStyle}>Contact Information</h3>
                  <p style={sectionDescStyle}>
                    Contact details and emergency contact
                  </p>
                </div>

                <div style={twoColGridStyle}>
                  <div style={inputGroupStyle}>
                    <label style={labelStyle} htmlFor="phone">
                      Phone Number{" "}
                      <span style={{ color: colorTheme.error }}>*</span>
                    </label>
                    {(errors.phone || sectionErrors.phone) && (
                      <FieldError error={errors.phone || sectionErrors.phone} />
                    )}
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      style={getInputStyle(
                        !!(errors.phone || sectionErrors.phone)
                      )}
                      placeholder="71300867"
                    />
                  </div>

                  <div style={inputGroupStyle}>
                    <label style={labelStyle} htmlFor="email">
                      Email Address
                    </label>
                    {(errors.email || sectionErrors.email) && (
                      <FieldError error={errors.email || sectionErrors.email} />
                    )}
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      style={getInputStyle(
                        !!(errors.email || sectionErrors.email)
                      )}
                      placeholder="patient@email.com"
                      autoComplete="email"
                    />
                  </div>
                </div>

                <div style={inputGroupStyle}>
                  <label style={labelStyle} htmlFor="address">
                    Address <span style={{ color: colorTheme.error }}>*</span>
                  </label>
                  {(errors.address || sectionErrors.address) && (
                    <FieldError
                      error={errors.address || sectionErrors.address}
                    />
                  )}
                  <input
                    type="text"
                    id="address"
                    name="address"
                    list="city-suggestions"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="e.g., Tripoli, Beirut"
                    style={getInputStyle(
                      !!(errors.address || sectionErrors.address)
                    )}
                  />
                  <datalist id="city-suggestions">
                    <option value="Tripoli" />
                    <option value="Beirut" />
                  </datalist>
                </div>

                <div style={emergencyBoxStyle}>
                  <h4 style={emergencyTitleStyle}>
                    <span style={{ marginRight: "8px" }}>📞</span>
                    Emergency Contact
                  </h4>

                  <div style={threeColGridStyle}>
                    <div style={inputGroupStyle}>
                      <label style={labelStyle} htmlFor="emergencyContactName">
                        Full Name{" "}
                      </label>
                      {(errors.emergencyContactName ||
                        sectionErrors.emergencyContactName) && (
                        <FieldError
                          error={
                            errors.emergencyContactName ||
                            sectionErrors.emergencyContactName
                          }
                        />
                      )}
                      <input
                        type="text"
                        id="emergencyContactName"
                        name="emergencyContactName"
                        value={formData.emergencyContactName}
                        onChange={handleInputChange}
                        style={getInputStyle(
                          !!(
                            errors.emergencyContactName ||
                            sectionErrors.emergencyContactName
                          )
                        )}
                        placeholder="Emergency contact name"
                      />
                    </div>

                    <div style={inputGroupStyle}>
                      <label style={labelStyle} htmlFor="emergencyContactPhone">
                        Phone Number{" "}
                      </label>
                      {(errors.emergencyContactPhone ||
                        sectionErrors.emergencyContactPhone) && (
                        <FieldError
                          error={
                            errors.emergencyContactPhone ||
                            sectionErrors.emergencyContactPhone
                          }
                        />
                      )}
                      <input
                        type="tel"
                        id="emergencyContactPhone"
                        name="emergencyContactPhone"
                        value={formData.emergencyContactPhone}
                        onChange={handleInputChange}
                        style={getInputStyle(
                          !!(
                            errors.emergencyContactPhone ||
                            sectionErrors.emergencyContactPhone
                          )
                        )}
                        placeholder="71300867"
                      />
                    </div>

                    <div style={inputGroupStyle}>
                      <label
                        style={labelStyle}
                        htmlFor="emergencyContactRelationship"
                      >
                        Relationship{" "}
                      </label>
                      {(errors.emergencyContactRelationship ||
                        sectionErrors.emergencyContactRelationship) && (
                        <FieldError
                          error={
                            errors.emergencyContactRelationship ||
                            sectionErrors.emergencyContactRelationship
                          }
                        />
                      )}
                      <select
                        id="emergencyContactRelationship"
                        name="emergencyContactRelationship"
                        value={formData.emergencyContactRelationship}
                        onChange={handleInputChange}
                        style={getInputStyle(
                          !!(
                            errors.emergencyContactRelationship ||
                            sectionErrors.emergencyContactRelationship
                          )
                        )}
                      >
                        <option value="">Select relationship</option>
                        {relationships.map((rel) => (
                          <option key={rel} value={rel.toLowerCase()}>
                            {rel}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Section 3: Medical Information */}
            {currentSection === 3 && (
              <div>
                <div
                  style={{
                    ...sectionHeaderStyle,
                    borderLeftColor: colorTheme.error,
                  }}
                >
                  <h3 style={sectionTitleStyle}>Medical Information</h3>
                  <p style={sectionDescStyle}>
                    Health history and medical details
                  </p>
                </div>

                <div style={inputGroupStyle}>
                  <label
                    style={{
                      ...labelStyle,
                      display: "flex",
                      alignItems: "center",
                    }}
                    htmlFor="allergies"
                  >
                    <span style={{ marginRight: "8px" }}>❤️</span>
                    Known Allergies
                  </label>
                  <input
                    type="text"
                    id="allergies"
                    name="allergies"
                    value={formData.allergies}
                    onChange={handleInputChange}
                    style={getInputStyle(false)}
                    placeholder="e.g., Penicillin, Nuts, Pollen"
                  />
                  <p
                    style={{
                      fontSize: "14px",
                      color: colorTheme.gray500,
                      marginTop: "6px",
                    }}
                  >
                    Separate multiple allergies with commas
                  </p>
                </div>

                <div style={inputGroupStyle}>
                  <label style={labelStyle} htmlFor="chronicConditions">
                    Chronic Conditions
                  </label>
                  <input
                    type="text"
                    id="chronicConditions"
                    name="chronicConditions"
                    value={formData.chronicConditions}
                    onChange={handleInputChange}
                    style={getInputStyle(false)}
                    placeholder="e.g., Diabetes, Hypertension"
                  />
                  <p
                    style={{
                      fontSize: "14px",
                      color: colorTheme.gray500,
                      marginTop: "6px",
                    }}
                  >
                    Separate multiple conditions with commas
                  </p>
                </div>

                <div style={inputGroupStyle}>
                  <label style={labelStyle} htmlFor="medicalNotes">
                    Medical Notes
                  </label>
                  <textarea
                    id="medicalNotes"
                    name="medicalNotes"
                    value={formData.medicalNotes}
                    onChange={handleInputChange}
                    rows={4}
                    style={{
                      ...getInputStyle(false),
                      resize: "none",
                    }}
                    placeholder="Any additional medical information, current medications, or special notes..."
                  />
                </div>
              </div>
            )}

            {/* Section 4: Insurance Information */}
            {currentSection === 4 && (
              <div>
                <div
                  style={{
                    ...sectionHeaderStyle,
                    borderLeftColor: colorTheme.success,
                  }}
                >
                  <h3 style={sectionTitleStyle}>Insurance Information</h3>
                  <p style={sectionDescStyle}>Optional coverage details</p>
                </div>

                <div style={twoColGridStyle}>
                  <div style={inputGroupStyle}>
                    <label
                      style={{
                        ...labelStyle,
                        display: "flex",
                        alignItems: "center",
                      }}
                      htmlFor="insuranceProvider"
                    >
                      <span style={{ marginRight: "8px" }}>🛡️</span>
                      Insurance Provider
                    </label>
                    <select
                      id="insuranceProvider"
                      name="insuranceProvider"
                      value={formData.insuranceProvider}
                      onChange={handleInputChange}
                      style={getInputStyle(false)}
                    >
                      <option value="">Select insurance provider</option>
                      <optgroup label="Public / Governmental">
                        <option value="National Social Security Fund (NSSF)">
                          National Social Security Fund (NSSF)
                        </option>
                        <option value="Civil Servants Cooperative">
                          Civil Servants Cooperative
                        </option>
                        <option value="Ministry of Public Health (MoPH)">
                          Ministry of Public Health (MoPH)
                        </option>
                        <option value="Lebanese Army Medical Services">
                          Lebanese Army Medical Services
                        </option>
                        <option value="Internal Security Forces Medical Services">
                          Internal Security Forces Medical Services
                        </option>
                        <option value="General Security Medical Services">
                          General Security Medical Services
                        </option>
                        <option value="State Security Medical Services">
                          State Security Medical Services
                        </option>
                        <option value="Judges' Mutual Fund">
                          Judges' Mutual Fund
                        </option>
                        <option value="Lebanese University Professors' Mutual Fund">
                          Lebanese University Professors' Mutual Fund
                        </option>
                      </optgroup>
                      <optgroup label="Private">
                        <option value="private_other">
                          Private (Specify below)
                        </option>
                      </optgroup>
                    </select>
                  </div>

                  {/* Show custom input field when "Private" is selected */}
                  {formData.insuranceProvider === "private_other" && (
                    <div style={inputGroupStyle}>
                      <label
                        style={labelStyle}
                        htmlFor="insuranceProviderCustom"
                      >
                        Private Insurance Name
                      </label>
                      <input
                        type="text"
                        id="insuranceProviderCustom"
                        name="insuranceProviderCustom"
                        value={formData.insuranceProvider || ""}
                        onChange={handleInputChange}
                        style={getInputStyle(false)}
                        placeholder="Enter private insurance provider name"
                      />
                    </div>
                  )}

                  <div style={inputGroupStyle}>
                    <label style={labelStyle} htmlFor="insuranceNumber">
                      Insurance Number
                    </label>
                    <input
                      type="text"
                      id="insuranceNumber"
                      name="insuranceNumber"
                      value={formData.insuranceNumber}
                      onChange={handleInputChange}
                      style={getInputStyle(false)}
                      placeholder="Insurance policy number"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation */}
            <div style={navigationStyle} className="navigation-container">
              <button
                id="prevButton"
                name="prevButton"
                onClick={prevSection}
                disabled={currentSection === 1}
                style={getPrevButtonStyle(currentSection === 1)}
              >
                <span style={{ marginRight: "8px" }}>←</span>
                Previous
              </button>

              {currentSection < 4 ? (
                <button
                  id="nextButton"
                  name="nextButton"
                  onClick={nextSection}
                  style={getNextButtonStyle(false)}
                >
                  Next
                  <span style={{ marginLeft: "8px" }}>→</span>
                </button>
              ) : (
                <button
                  id="submitButton"
                  name="submitButton"
                  onClick={handleSubmit}
                  disabled={isLoading}
                  style={{
                    ...getNextButtonStyle(true),
                    opacity: isLoading ? 0.7 : 1,
                  }}
                >
                  {isLoading ? (
                    <>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: "2px solid white",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                          marginRight: "8px",
                        }}
                      />
                      Registering...
                    </>
                  ) : (
                    <>
                      <span style={{ marginRight: "8px" }}>✓</span>
                      Register Patient
                    </>
                  )}
                </button>
              )}
            </div>

            {errors.submit && (
              <div
                style={{
                  marginTop: "24px",
                  padding: "16px",
                  background: "#fef2f2",
                  border: "1px solid #fecaca",
                  borderRadius: "12px",
                  color: "#dc2626",
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <span style={{ marginRight: "8px", marginTop: "2px" }}>⚠️</span>
                {errors.submit}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          input:focus, select:focus, textarea:focus {
            border-color: ${colorTheme.primary} !important;
            box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1) !important;
          }
          
          button:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0,0,0,0.15);
          }

          /* Mobile responsive navigation */
          @media (max-width: 768px) {
            #prevButton, #nextButton, #submitButton {
              min-width: 120px;
              margin: 4px;
            }
            
            /* Make select dropdowns more mobile friendly */
            select {
              font-size: 16px !important;
              padding: 16px 18px !important;
              min-height: 50px;
              -webkit-appearance: none;
              -moz-appearance: none;
              appearance: none;
              background-image: url('data:image/svg+xml;charset=US-ASCII,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4 5"><path fill="%23666" d="M2 0L0 2h4zm0 5L0 3h4z"/></svg>');
              background-repeat: no-repeat;
              background-position: right 12px center;
              background-size: 12px;
              padding-right: 40px !important;
            }
          }

          @media (max-width: 480px) {
            .navigation-container {
              flex-direction: column;
              gap: 16px !important;
              align-items: stretch;
            }
            
            #prevButton, #nextButton, #submitButton {
              width: 100%;
              justify-content: center;
              margin: 0;
            }
            
            /* Extra mobile optimization for selects */
            select {
              font-size: 16px !important;
              padding: 18px 20px !important;
              min-height: 54px;
              border-radius: 8px !important;
            }
            
            /* Make form content more mobile friendly */
            .form-content {
              padding: 24px 16px !important;
            }
            
            /* Adjust grid layouts for mobile */
            .two-col-grid {
              grid-template-columns: 1fr !important;
              gap: 24px !important;
            }
            
            .three-col-grid {
              grid-template-columns: 1fr !important;
              gap: 24px !important;
            }
          }
        `}
      </style>
    </div>
  );
};

export default PatientRegistrationForm;
